import React from 'react';
import { Typography } from '@notch-ordering/ui-components';
import { formatAsCurrency } from '@/utils/Formatters';

interface CreditInputProps {
    availableCredits: number,
    maxApplicableCredits: number,
    creditToApply: number,
    creditInputValue: string,
    onCreditChange: (value: number, inputValue: string) => void,
    supplierName?: string,
}

export const CreditInput: React.FC<CreditInputProps> = ({
    availableCredits,
    maxApplicableCredits,
    creditToApply,
    creditInputValue,
    onCreditChange,
    supplierName,
}) => {
    const handleBlur = (): void => {
        onCreditChange(creditToApply, creditToApply.toFixed(2));
    };

    return (
        <div>
            <div className="mb-4">
                {/* Label + input (aligned like in the design) */}
                <div className="flex items-center justify-between mb-1">
                    <div className="min-w-0">
                        <Typography variant="LG-1" weight="font-semibold" className=" truncate m-0">
                            {/* Per design screenshot we keep label concise; supplier name is optional and can be appended if desired */}
                            {supplierName ? `Apply credit — ${supplierName}` : 'Apply credit'}
                        </Typography>
                        {/* Available credit helper text */}
                        <Typography variant="BASE" weight="font-regular" className="text-gray-650 m-0">
                            Available credit: {formatAsCurrency(availableCredits)}
                        </Typography>
                    </div>
                    <div className="relative w-32 sm:w-36">
                        <input
                            aria-label="Apply credit amount"
                            inputMode="decimal"
                            type="text"
                            value={`$${creditInputValue}`}
                            onChange={(e) => {
                                const rawValue = e.target.value.replace(/^\$/, '').replace(/[^0-9.]/g, '');
                                const numeric = rawValue === '' ? 0 : Number(rawValue);
                                const numValue = Number.isFinite(numeric) ? numeric : 0;
                                const clampedValue = Math.min(Math.max(0, numValue), maxApplicableCredits);
                                onCreditChange(clampedValue, rawValue);
                            }}
                            onBlur={handleBlur}
                            className="w-full text-right pr-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    </div>
                </div>
            </div>
        </div>
    );
};
